import { cn } from "@newxsystems/shared";
import { getComponent } from "../../../../client";
import { RavioliMedia } from "../RavioliMedia/RavioliMedia";
import type { ILogoCloudGridProps } from "./LogoCloudGrid.props";

const desktopColumns = {
  1: "md:grid-cols-1",
  2: "md:grid-cols-2",
  3: "md:grid-cols-3",
  4: "md:grid-cols-4",
} as const;

type DesktopColumnCount = keyof typeof desktopColumns;

const getGridScore = (itemCount: number, columns: 3 | 4) => {
  const lastRowItems = itemCount % columns || columns;

  return [
    Number(lastRowItems === 1),
    Number(lastRowItems !== columns),
    columns - lastRowItems,
    Math.ceil(itemCount / columns),
  ] as const;
};

const getDesktopColumnCount = (itemCount: number): DesktopColumnCount => {
  if (itemCount <= 1) return 1;
  if (itemCount <= 4) return itemCount as DesktopColumnCount;

  const threeColumnScore = getGridScore(itemCount, 3);
  const fourColumnScore = getGridScore(itemCount, 4);

  for (let index = 0; index < threeColumnScore.length; index += 1) {
    if (threeColumnScore[index] !== fourColumnScore[index]) {
      return threeColumnScore[index] < fourColumnScore[index] ? 3 : 4;
    }
  }

  return 4;
};

export const LogoCloudGrid = ({ items }: ILogoCloudGridProps) => {
  const Link = getComponent("Link");
  const desktopLayout = desktopColumns[getDesktopColumnCount(items.length)];
  return (
    <div
      className={cn(
        "grid gap-3",
        items.length === 1 ? "grid-cols-1" : "grid-cols-2",
        desktopLayout,
      )}
    >
      {items.map((item, index) => {
        const logo = (
          <RavioliMedia
            className={cn(
              "h-28 border border-primary p-6",
              index % 2 === 0 ? "bg-surface" : "bg-collection",
            )}
            imageClassName="object-contain transition-[filter,transform] duration-200 group-hover:scale-105"
            media={item.logo}
            sizes="180px"
          />
        );
        return (
          <div key={index}>
            {item.logoLink ? (
              <Link
                aria-label={item.logoLink.accessibilityLabel || item.logoLink.label}
                className="group block focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                href={item.logoLink.href}
                rel={item.logoLink.openInNewTab ? "noopener noreferrer" : undefined}
                target={item.logoLink.openInNewTab ? "_blank" : undefined}
              >
                {logo}
              </Link>
            ) : (
              logo
            )}
          </div>
        );
      })}
    </div>
  );
};

LogoCloudGrid.displayName = "Content::LogoCloudGrid";
